import { useIsRollsReceivedImportMode } from "@/pages/production/rolls-received/hooks/useRollsReceived";
import {
	OPERATIONAL_WRITES_LOCKED_MESSAGE,
	useOperationalWritesLocked,
} from "@/shared/hooks/useOperationalWritesLocked";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Paper } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import type { GridApi } from "ag-grid-community";
import moment from "moment";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	isUsableDate,
	urlParam,
	useUrlState,
	useUrlText,
} from "@/shared/hooks/useUrlState";
import { formatProxyDate } from "@/shared/utils/proxyDateTime";
import {
	useDeleteRoll,
	useMillSizes,
	useRollsList,
	useUpsertRoll,
} from "../hooks/useRollshopOperations";
import type { RollListItem } from "../models/RollshopOperations";
import type { RollUpsertPayload } from "../repositories/RollshopOperationsRepository";
import { RollDetailPanel } from "./RollDetailPanel";
import { RollsTable } from "./RollsTable";
import { RollsToolbar } from "./RollsToolbar";

// Master on the LEFT (table dominates with 19 columns), detail panel on
// the RIGHT (form for Add/Edit). Mirrors the Rollsets layout.
const DEFAULT_LAYOUT = { left: 65, right: 35 };

const matchesSearch = (row: RollListItem, q: string): boolean => {
	if (!q) return true;
	const needle = q.toLowerCase();
	return (
		(row.Name?.toLowerCase().includes(needle) ?? false) ||
		(row.Description?.toLowerCase().includes(needle) ?? false) ||
		(row.SupplierName?.toLowerCase().includes(needle) ?? false) ||
		(row.PurchaseOrderName?.toLowerCase().includes(needle) ?? false)
	);
};

// Empty roll used to seed the form for an "Add new" action. Id=0 marks
// the row as new (the SP treats Id IS NULL/<=0 as INSERT).
const EMPTY_ROLL: RollListItem = {
	Id: 0,
	Name: "",
	Description: null,
	StatusCode: "FREE",
	StatusName: null,
	Diameter: null,
	BlankDiameter: null,
	NewDiameter: null,
	ScrapDiameter: null,
	Hardness: null,
	Width: null,
	MillSizeId: null,
	MillSizeName: null,
	RollApplicationId: null,
	RollApplicationName: null,
	RollMaterialId: null,
	RollMaterialName: null,
	Cost: null,
	CurrentCost: null,
	CurrentPercentageLeft: null,
	CostCenterId: null,
	CostCenterName: null,
	PurchaseOrderId: null,
	PurchaseOrderName: null,
	PurchaseOrderItemId: null,
	PurchaseOrderItemNumber: null,
	SupplierId: null,
	SupplierName: null,
	SupplierInvoiceNumber: null,
	OrderStatusCode: null,
	OrderStatusName: null,
	OrderTimestamp: null,
	OrderTimestampString: null,
	ReceiveTimestamp: null,
	ReceiveTimestampString: null,
	InvoiceTimestamp: null,
	InvoiceTimestampString: null,
	FirstTurnedTimestamp: null,
	FirstTurnedTimestampString: null,
	LastTurnedTimestamp: null,
	LastTurnedTimestampString: null,
	ReturnedDateString: null,
	BarrelAssignedId: null,
	BarrelAssignedName: null,
	RollsetAssignedId: null,
	RollsetAssignedName: null,
	WasTurned: 0,
};

const URL_SCHEMA = {
	// The Receive Date range defaults to the last year: it trims the result set so the page
	// does not haul ~5 MB of historical rolls every load.
	receiveStart: urlParam.date(() =>
		moment().subtract(1, "year").startOf("day"),
	),
	receiveEnd: urlParam.date(() => moment().startOf("day")),
	search: urlParam.string(),
	millSizeId: urlParam.int(),
	statusCode: urlParam.string(),
	rollId: urlParam.int(),
};

export const Rolls = () => {
	const importMode = useIsRollsReceivedImportMode();
	const writesLocked = useOperationalWritesLocked();
	const readOnly = writesLocked || importMode;
	const readOnlyReason = writesLocked
		? OPERATIONAL_WRITES_LOCKED_MESSAGE
		: "Rolls are owned by RollShop import";
	const [
		{
			receiveStart: receiveDateFrom,
			receiveEnd: receiveDateTo,
			search,
			millSizeId,
			statusCode,
			rollId,
		},
		setFilters,
		{ dropValues },
	] = useUrlState(URL_SCHEMA);
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	const [errorMsg, setErrorMsg] = useState("");
	const [notification, setNotification] = useState("");
	// Only the roll being added, or the one just created, lives here. An existing selection
	// is the id in the URL, and Id=0 never belongs in a link.
	const [draft, setDraft] = useState<RollListItem | null>(null);
	const [rollToDelete, setRollToDelete] = useState<RollListItem | null>(null);

	const receiveDateFromStr = receiveDateFrom.format("YYYY-MM-DD");
	const receiveDateToStr = receiveDateTo.format("YYYY-MM-DD");

	const { data, isFetching, isSuccess, error, refetch } = useRollsList(
		receiveDateFromStr,
		receiveDateToStr,
	);
	const millSizesQuery = useMillSizes();
	const gridApiRef = useRef<GridApi<RollListItem> | null>(null);
	// Stash the latest upsert payload so onSuccess can rebuild a synthetic
	// row after CREATE — avoids a flash of empty fields between the save
	// returning the new Id and the list refetch landing.
	const lastPayloadRef = useRef<RollUpsertPayload | null>(null);

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "rollshop-operations-rolls-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const filteredRows = useMemo(() => {
		if (!data) return [];
		// Receive Date range is also applied client-side: in legacy mode the
		// proxy calls [CAT].[GetRolls], which silently ignores the date
		// params, so without this filter the toolbar pickers would have no
		// effect on rolls received outside the chosen window.
		return data.filter((row) => {
			const recv = row.ReceiveTimestamp;
			if (recv == null) return false;
			const recvDate = recv.slice(0, 10);
			if (recvDate < receiveDateFromStr || recvDate > receiveDateToStr)
				return false;
			return (
				matchesSearch(row, searchText) &&
				(statusCode == null || row.StatusCode === statusCode) &&
				(millSizeId == null || row.MillSizeId === millSizeId)
			);
		});
	}, [
		data,
		searchText,
		statusCode,
		millSizeId,
		receiveDateFromStr,
		receiveDateToStr,
	]);

	// A draft wins while it is unsaved. Otherwise the id in the URL is resolved against the
	// rolls the server returned rather than the filtered view, so typing in Search narrows
	// the grid without closing the panel.
	const syncedSelected = useMemo(() => {
		if (draft) return draft;
		if (rollId === null) return null;
		return (data ?? []).find((r) => r.Id === rollId) ?? null;
	}, [draft, data, rollId]);

	// A link to a roll outside the loaded range would leave the panel empty, so the key goes.
	useEffect(() => {
		if (!isSuccess || rollId === null || draft) return;
		if (!(data ?? []).some((r) => r.Id === rollId)) dropValues("rollId");
	}, [isSuccess, data, rollId, draft, dropValues]);

	// The roll just created stays on screen until the refetched list carries it.
	useEffect(() => {
		if (!draft || draft.Id <= 0) return;
		if ((data ?? []).some((r) => r.Id === draft.Id)) setDraft(null);
	}, [data, draft]);

	const { upsertRoll, isUpserting } = useUpsertRoll({
		onSuccess: (data) => {
			const wasNew = !draft?.Id || draft.Id <= 0;
			setNotification(
				wasNew ? "Roll created successfully" : "Roll updated successfully",
			);
			// CREATE: synthesise a row from the just-saved payload so the
			// detail panel keeps showing the user's values while the list
			// refetches in the background. Without this the form would
			// flash empty after CREATE.
			// Existing autosave: leave `selected` untouched. The list
			// refetch will refresh `syncedSelected`, but the detail form's
			// useEffect is keyed on `roll?.Id` so it won't repopulate while
			// the user keeps editing the same row.
			if (wasNew && data?.Id) {
				const p = lastPayloadRef.current;
				setFilters({ rollId: data.Id });
				setDraft({
					...EMPTY_ROLL,
					Name: p?.Name ?? "",
					Description: p?.Description ?? null,
					RollApplicationId: p?.RollApplicationId ?? null,
					BlankDiameter: p?.BlankDiameter ?? null,
					Diameter: p?.Diameter ?? null,
					Hardness: p?.Hardness ?? null,
					RollMaterialId: p?.RollMaterialId ?? null,
					MillSizeId: p?.MillSizeId ?? null,
					Cost: p?.Cost ?? null,
					CostCenterId: p?.CostCenterId ?? null,
					OrderTimestamp: p?.OrderTimestamp ?? null,
					ReceiveTimestamp: p?.ReceiveTimestamp ?? null,
					InvoiceTimestamp: p?.InvoiceTimestamp ?? null,
					PurchaseOrderItemId: p?.PurchaseOrderItemId ?? null,
					SupplierId: p?.SupplierId ?? null,
					SupplierInvoiceNumber: p?.SupplierInvoiceNumber ?? null,
					Id: data.Id,
				});
			}
			lastPayloadRef.current = null;
		},
		onError: (e) => {
			setErrorMsg(e.message);
			lastPayloadRef.current = null;
		},
	});

	const { deleteRoll, isDeleting } = useDeleteRoll({
		onSuccess: () => {
			setNotification("Roll deleted successfully");
			setRollToDelete(null);
			setDraft(null);
			dropValues("rollId");
		},
		onError: (e) => {
			setErrorMsg(e.message);
			setRollToDelete(null);
		},
	});

	const handleRefresh = useCallback(() => refetch(), [refetch]);

	const handleAdd = useCallback(() => {
		setDraft({ ...EMPTY_ROLL });
		dropValues("rollId");
	}, [dropValues]);

	const handleAskDelete = useCallback((row: RollListItem) => {
		setRollToDelete(row);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (rollToDelete?.Id != null && rollToDelete.Id > 0) {
			deleteRoll(rollToDelete.Id);
		}
	}, [rollToDelete, deleteRoll]);

	const handleSave = useCallback(
		(payload: RollUpsertPayload) => {
			lastPayloadRef.current = payload;
			upsertRoll(payload);
		},
		[upsertRoll],
	);

	// Date columns are stored as strings already formatted by the SP — Excel
	// export reuses the same `formatProxyDate` slice the grid uses.
	const handleExportExcel = useCallback(() => {
		if (!gridApiRef.current) return;
		gridApiRef.current.exportDataAsExcel({
			fileName: "Rolls.xlsx",
			processCellCallback: (p) => {
				const colId = p.column.getColId();
				if (
					colId === "OrderTimestamp" ||
					colId === "ReceiveTimestamp" ||
					colId === "InvoiceTimestamp"
				) {
					return formatProxyDate(p.value);
				}
				if (colId === "Actions") {
					return "";
				}
				return p.value ?? "";
			},
		});
	}, []);

	if (error && !errorMsg) {
		setErrorMsg(
			error instanceof Error ? error.message : "Error fetching rolls",
		);
	}

	return (
		<Box
			sx={{
				flex: 1,
				display: "flex",
				flexDirection: "column",
				minHeight: 0,
				gap: 1,
			}}
		>
			<Paper elevation={3}>
				<RollsToolbar
					receiveDateFrom={receiveDateFrom}
					receiveDateTo={receiveDateTo}
					onReceiveDateFromChange={(d) =>
						isUsableDate(d) && d && setFilters({ receiveStart: d })
					}
					onReceiveDateToChange={(d) =>
						isUsableDate(d) && d && setFilters({ receiveEnd: d })
					}
					search={searchText}
					onSearchChange={setSearchText}
					millSizeId={millSizeId}
					onMillSizeChange={(id) => setFilters({ millSizeId: id })}
					statusCode={statusCode}
					onStatusChange={(code) => setFilters({ statusCode: code })}
					millSizes={millSizesQuery.data ?? []}
					onRefresh={handleRefresh}
					onExportExcel={handleExportExcel}
					onAdd={handleAdd}
					addDisabled={readOnly}
					addDisabledReason={readOnlyReason}
					isLoading={isFetching}
				/>
			</Paper>

			<Group
				id="rolls-master-detail"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={35} style={{ paddingBottom: 5 }}>
					<Box sx={{ height: "100%", pr: 1 }}>
						<Paper elevation={3} sx={{ height: "100%", overflow: "hidden" }}>
							<RollsTable
								rows={filteredRows}
								isLoading={isFetching}
								selectedId={syncedSelected?.Id ?? null}
								onRowClick={(row) => {
									setDraft(null);
									setFilters({ rollId: row.Id });
								}}
								onGridReady={(api) => {
									gridApiRef.current = api;
								}}
								onDelete={handleAskDelete}
								deleteDisabled={readOnly}
								deleteDisabledReason={readOnlyReason}
							/>
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 6,
							height: 40,
							borderRadius: 3,
							backgroundColor: "divider",
							"&:hover": { backgroundColor: "primary.main" },
						}}
					/>
				</Separator>

				<Panel id="right" minSize={25} style={{ paddingBottom: 5 }}>
					<Box sx={{ height: "100%", pl: 1 }}>
						<Paper
							elevation={3}
							sx={{
								height: "100%",
								overflowY: "auto",
								overflowX: "hidden",
							}}
						>
							<RollDetailPanel
								roll={syncedSelected}
								disabled={readOnly}
								disabledReason={readOnlyReason}
								isSaving={isUpserting || isDeleting}
								onSave={handleSave}
								onClose={() => {
									setDraft(null);
									dropValues("rollId");
								}}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<DeleteConfirmModal
				open={rollToDelete !== null}
				title="DELETE ROLL"
				itemName={rollToDelete?.Name ?? ""}
				isDeleting={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={() => setRollToDelete(null)}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
